import * as S from "./styles.ts";
import { useState } from "react";
import { ColorKey } from "@template/theme/types/color.type.ts";
import SliderPart from "@template/components/Slider/SliderPart";
import SliderLabel from "@template/components/Slider/SliderLabel";
import { Pill } from "@template/components";
import { useTypedSelector } from "@/redux/helpers/hooks.ts";

export interface ISliderProps {
  value?: number;
  max?: number;
  min?: number;
  step?: number;
  onChange?: (value: number | any, e?: any) => void;
  breaks?: number[];
  color?: ColorKey;
  showNumbers?: boolean;
  startText?: string;
  showValue?: boolean;
  altStyling?: boolean;
}

interface IStartProps {
  startText: string;
  onClick: (e: any) => void;
}

const Start = ({ startText, onClick }: IStartProps) => (
  <S.StartWrapper onClick={onClick}>
    <SliderPart
      span={1}
      percentageFilled={1}
      isLast={false}
    />
    <S.StartLabelWrapper>
      <SliderLabel
        label={startText}
        onClick={() => {}}
      />
    </S.StartLabelWrapper>
  </S.StartWrapper>
);

const Slider = ({
  value,
  max = 100,
  min = 1,
  step = 1,
  onChange,
  breaks: breakPoints,
  showNumbers = true,
  color = "primary",
  startText,
  showValue = true,
  altStyling = false,
}: ISliderProps) => {
  const [defaultValue, setDefaultValue] = useState(min);
  const user = useTypedSelector((state) => state.user.user);
  const breaks = [...(breakPoints || []), max]
    .filter((value, index, self) => self.indexOf(value) === index)
    .sort((a, b) => a - b);

  const handleBreakpoint = (breakpoint: number, index: number) => {
    if (index === breaks.length - 1) {
      return max - min - (breaks?.[index - 1] || 0);
    }

    return breakpoint - (breaks[index - 1] || 0);
  };

  const usedValue = value || defaultValue;

  const handleChange = (val: number | undefined, e: any) => {
    if (val === undefined) return;
    setDefaultValue(val);
    onChange && onChange(val, e);
  };

  return (
    <S.SliderWrapper
      $color={user?.has_voip && user?.voip_plan !== 8 ? "default" : color}
      $isUser={user?.has_voip && user?.voip_plan !== 8}
      $altStyling={altStyling}
    >
      {startText && (
        <Start
          startText={startText}
          onClick={(e) => handleChange(min, e)}
        />
      )}
      <S.SliderContainer>
        <S.SliderPartsContainer
          $max={max - min}
          $current={value}
        >
          {breaks.map((breakpoint, index) => {
            const span = handleBreakpoint(breakpoint, index);

            if (!span) return;

            return (
              <SliderPart
                span={span}
                percentageFilled={
                  usedValue === 50 && index === breaks.length - 1
                    ? 1
                    : Math.max(
                        0,
                        Math.min(
                          1,
                          (usedValue - min - (breaks[index - 1] || 0)) / span
                        )
                      )
                }
                key={index}
                isLast={index === breaks.length - 1}
                altStyling={altStyling}
              />
            );
          })}
        </S.SliderPartsContainer>
        {/* {showNumbers && (
          <S.SliderLabelsWrapper>
            {[startText ? undefined : 1, ...breaks].map((breakpoint, index) => (
              <SliderLabel
                key={index}
                label={breakpoint}
                onClick={(e) => handleChange(breakpoint, e)}
                left={
                  //percentage of the breakpoint, from the min to the max
                  index === 0
                    ? 0
                    : index === breaks.length
                      ? 100
                      : ((breakpoint || 0) / (max - min)) * 100
                }
              />
            ))}
          </S.SliderLabelsWrapper>
        )} */}
        {showNumbers && (
          <S.SliderLabelsWrapper>
            {!startText && (
              <SliderLabel
                label={min}
                onClick={(e) => handleChange(min, e)}
                disabled={user?.has_voip && user?.voip_plan !== 8}
                left={0}
              />
            )}

            {breaks
              .filter((point) => point !== max) // prevent showing 52
              .map((point, index) => (
                <SliderLabel
                  key={`break-${index}`}
                  label={point}
                  onClick={(e) => handleChange(point, e)}
                  disabled={user?.has_voip && user?.voip_plan !== 8}
                  left={((point - min) / (max - min)) * 100}
                />
              ))}

            {/* Manually render the last label as "50" at 50's position */}
            <SliderLabel
              label={"50"}
              onClick={(e) => handleChange(50, e)}
              disabled={user?.has_voip && user?.voip_plan !== 8}
              left={((50 - min) / (max - min)) * 100}
            />
          </S.SliderLabelsWrapper>
        )}

        <S.SliderThumb
          style={{
            left: `${((usedValue - min) / (51 - min)) * 100}%`,
          }}
          $altStyling={altStyling}
        >
          {showValue && (
            <S.ValueContainer>
              <Pill
                color={
                  user?.has_voip && user?.voip_plan !== 8 ? "default" : color
                }
                variant={
                  user?.has_voip && user?.voip_plan !== 8 ? "flat" : "solid"
                }
              >
                {usedValue >= 50 ? "50" : usedValue}
              </Pill>
            </S.ValueContainer>
          )}
        </S.SliderThumb>
        <S.RangeInput
          aria-label="Range slider to select number of users"
          type="range"
          min={min}
          max={max}
          step={step}
          value={defaultValue}
          disabled={user?.has_voip && user?.voip_plan !== 8}
          onChange={(e) => handleChange(Number(e.target.value), e)}
        />
      </S.SliderContainer>
    </S.SliderWrapper>
  );
};

export default Slider;
